import React, { useCallback, useEffect, useState } from 'react';
import {
  FaBug,
  FaCheckCircle,
  FaCommentDots,
  FaEnvelopeOpenText,
  FaLightbulb,
  FaLock,
  FaPaperPlane,
  FaQuestionCircle,
  FaRegSmile,
  FaShieldAlt,
  FaSpinner,
  FaUserCheck,
} from 'react-icons/fa';
import Seo from '../components/Seo';
import {
  FEEDBACK_CATEGORIES,
  MESSAGE_MAX,
  SUBJECT_MAX,
  fetchMyFeedback,
  getCategoryClass,
  getCategoryLabel,
  submitFeedback,
  validateFeedback,
} from '../services/feedback';
import './FeedbackPage.css';

const CATEGORY_ICONS = {
  suggestion: FaLightbulb,
  bug: FaBug,
  question: FaQuestionCircle,
  compliment: FaRegSmile,
  other: FaCommentDots,
};

function parseDate(value) {
  if (!value) return null;
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    const date = new Date(year, month - 1, day, hour, minute, second);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function FeedbackPage({ user, notify }) {
  const [category, setCategory] = useState('suggestion');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myItems, setMyItems] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [maxPerUser, setMaxPerUser] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const atLimit = remaining === 0;

  const loadMine = useCallback(async () => {
    if (!user) {
      setMyItems([]);
      setMaxPerUser(null);
      setRemaining(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoadingMine(true);
    try {
      const result = await fetchMyFeedback(token);
      setMyItems(result.items || []);
      setMaxPerUser(result.maxPerUser);
      setRemaining(result.remaining);
    } catch {
      setMyItems([]);
      setMaxPerUser(null);
      setRemaining(null);
    } finally {
      setLoadingMine(false);
    }
  }, [user]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const handleLogin = () => {
    try {
      sessionStorage.setItem('postLoginRedirect', '/feedback');
      window.dispatchEvent(new Event('open-auth-modal'));
    } catch {
      window.dispatchEvent(new Event('open-auth-modal'));
    }
  };

  const clearStatus = () => {
    if (error) setError('');
    if (submitted) setSubmitted(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!user) {
      handleLogin();
      return;
    }
    if (atLimit) {
      setError(`You have reached the limit of ${maxPerUser} feedback submissions.`);
      return;
    }

    const { ok, errors, values } = validateFeedback({ category, subject, message });
    if (!ok) {
      setError(errors[0]);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const result = await submitFeedback(
        {
          userId: user.id || user._id || user.userId,
          category: values.category,
          subject: values.subject,
          message: values.message,
        },
        token
      );
      setSubmitted(true);
      setSubject('');
      setMessage('');
      setCategory('suggestion');
      if (typeof notify === 'function') {
        notify(result.message, 'success');
      }
      const optimistic = {
        id: result.item?.id || `local-${Date.now()}`,
        category: values.category,
        subject: values.subject,
        message: values.message,
        createdAt: result.item?.createdAt || new Date().toISOString(),
      };
      setMyItems((prev) => [optimistic, ...prev]);
      setRemaining((prev) => (prev == null ? prev : Math.max(prev - 1, 0)));
    } catch (err) {
      const fallback = err.message || 'Unable to submit feedback. Please try again.';
      setError(fallback);
      if (typeof notify === 'function') {
        notify(fallback, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Send Feedback"
        description="Share product feedback, report issues, or suggest improvements for KeepMyAsset."
        path="/feedback"
        noindex
      />
      <div className="feedback-page">
        <div className="feedback-shell">
          <header className="feedback-header">
            <div className="feedback-header-copy">
              <p className="feedback-eyebrow">Feedback</p>
              <h1>
                <span className="feedback-title-icon" aria-hidden="true">
                  <FaCommentDots />
                </span>
                Help us improve KeepMyAsset
              </h1>
              <p className="feedback-subtitle">
                Tell us what is working, what is confusing, or what you would like next. We read every submission.
              </p>
              <ul className="feedback-trust">
                <li>
                  <FaUserCheck aria-hidden="true" />
                  Linked to your account
                </li>
                <li>
                  <FaShieldAlt aria-hidden="true" />
                  Private to our team
                </li>
                <li>
                  <FaEnvelopeOpenText aria-hidden="true" />
                  We follow up when needed
                </li>
              </ul>
            </div>
          </header>

          {!user ? (
            <div className="feedback-gate">
              <span className="feedback-gate-icon" aria-hidden="true">
                <FaLock />
              </span>
              <h2>Sign in to send feedback</h2>
              <p>
                You need to be logged in so we can follow up if we have questions. Your name and email are taken from your account.
              </p>
              <button type="button" className="feedback-primary-btn" onClick={handleLogin}>
                Login to continue
              </button>
            </div>
          ) : (
            <div className="feedback-layout">
              <form className="feedback-form" onSubmit={handleSubmit} noValidate>
                {submitted ? (
                  <div className="feedback-success" role="status">
                    <FaCheckCircle aria-hidden="true" />
                    <div>
                      <strong>Thanks — we received your feedback.</strong>
                      <p>You can send another note anytime if something else comes up.</p>
                    </div>
                  </div>
                ) : null}

                <fieldset className="feedback-fieldset">
                  <legend>What kind of feedback is this?</legend>
                  <div className="feedback-category-grid" role="radiogroup" aria-label="Feedback category">
                    {FEEDBACK_CATEGORIES.map(({ id, label, hint }) => {
                      const Icon = CATEGORY_ICONS[id] || FaCommentDots;
                      const selected = category === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          className={`feedback-category-card ${id} ${selected ? 'active' : ''}`}
                          disabled={atLimit}
                          onClick={() => {
                            setCategory(id);
                            clearStatus();
                          }}
                        >
                          <span className="feedback-category-icon" aria-hidden="true">
                            <Icon />
                          </span>
                          <strong>{label}</strong>
                          <small>{hint}</small>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <label className="feedback-field" htmlFor="feedback-subject">
                  <span>Subject</span>
                  <input
                    id="feedback-subject"
                    type="text"
                    value={subject}
                    maxLength={SUBJECT_MAX}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      clearStatus();
                    }}
                    placeholder="Short summary of your feedback"
                    disabled={atLimit}
                  />
                  <small>{subject.trim().length}/{SUBJECT_MAX}</small>
                </label>

                <label className="feedback-field" htmlFor="feedback-message">
                  <span>Details</span>
                  <textarea
                    id="feedback-message"
                    rows={7}
                    value={message}
                    maxLength={MESSAGE_MAX}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      clearStatus();
                    }}
                    placeholder="What happened, what you expected, or what you would like us to add…"
                    disabled={atLimit}
                  />
                  <small>{message.trim().length}/{MESSAGE_MAX}</small>
                </label>

                {error ? (
                  <p className="feedback-error" role="alert">{error}</p>
                ) : atLimit ? (
                  <p className="feedback-error" role="status">
                    You have reached the limit of {maxPerUser} feedback submissions.
                  </p>
                ) : null}

                <div className="feedback-actions">
                  <p className="feedback-actions-note">
                    {remaining != null && maxPerUser != null
                      ? `${remaining} of ${maxPerUser} submissions remaining.`
                      : 'We usually reply only if we need more detail.'}
                  </p>
                  <button type="submit" className="feedback-primary-btn" disabled={submitting || atLimit}>
                    {submitting ? <FaSpinner className="feedback-spin" aria-hidden="true" /> : <FaPaperPlane aria-hidden="true" />}
                    {submitting ? 'Sending…' : atLimit ? 'Limit reached' : 'Submit feedback'}
                  </button>
                </div>
              </form>

              <aside className="feedback-aside" aria-label="What happens next">
                <h2>What happens next</h2>
                <ol className="feedback-steps">
                  <li>
                    <strong>We read it</strong>
                    <span>Every note is reviewed by the KeepMyAsset team.</span>
                  </li>
                  <li>
                    <strong>We group similar ideas</strong>
                    <span>Bugs, questions, and requests are routed to the right place.</span>
                  </li>
                  <li>
                    <strong>We follow up if needed</strong>
                    <span>If we have a question, we will reach you on your account email.</span>
                  </li>
                </ol>
              </aside>
            </div>
          )}

          {user && (loadingMine || myItems.length > 0) ? (
            <section className="feedback-history" aria-label="Your previous feedback">
              <div className="feedback-history-head">
                <h2>Your previous feedback</h2>
                <p>
                  {loadingMine && myItems.length === 0
                    ? 'Loading your submissions…'
                    : maxPerUser != null
                      ? `${myItems.length} of ${maxPerUser} submitted`
                      : `${myItems.length} submitted`}
                </p>
              </div>
              {loadingMine && myItems.length === 0 ? (
                <p className="feedback-history-empty">Loading your submissions…</p>
              ) : (
                <ul className="feedback-history-list">
                  {myItems.map((item) => {
                    const categoryClass = getCategoryClass(item.category);
                    const when = formatDateTime(item.createdAt || item.created_at || item.submittedAt);
                    return (
                      <li key={item.id || `${item.subject}-${when}`} className={categoryClass}>
                        <div className="feedback-history-top">
                          <span className={`feedback-category-badge ${categoryClass}`}>
                            {getCategoryLabel(item.category)}
                          </span>
                          {when ? <time>{when}</time> : null}
                        </div>
                        <strong>{item.subject || 'Feedback'}</strong>
                        <p>{item.message || item.details || ''}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}

export default FeedbackPage;
